import { ColDef, ICellEditorParams } from 'ag-grid-community';
import { IntlShape } from 'react-intl';
import { ObtChannelFormat, ObtPartnerChannel } from '../../../__generated__/types';
import { PartnerIdentity } from './ObtPartnerDetailsTab';
import dayjs from 'dayjs';

const DATE_DISPLAY_FORMAT = 'DD.MM.YYYY';
const DATE_ISO_FORMAT = 'YYYY-MM-DD';

// ─── Cascade Helpers ────────────────────────────────────────────────────────────

/**
 * Get all unique inv process codes from the channel format data.
 */
export function getInvProcessCodes(channelFormat: ObtChannelFormat[]): string[] {
    const codes: string[] = [];
    for (const cf of channelFormat) {
        if (cf.invProcess) {
            codes.push(...cf.invProcess);
        }
    }
    return Array.from(new Set(codes));
}

/**
 * Get available directions for a given inv process.
 */
export function getDirectionsForProcess(channelFormat: ObtChannelFormat[], invprocess: string): string[] {
    if (!invprocess) return [];
    const match = channelFormat.find((cf) => cf.invProcess?.includes(invprocess));
    if (!match?.direction) return [];
    return match.direction.split(',').filter(Boolean);
}

/**
 * Get available providers for a given inv process + direction.
 */
export function getProvidersForDirection(
    channelFormat: ObtChannelFormat[],
    invprocess: string,
    direction: string
): string[] {
    if (!invprocess || !direction) return [];
    const match = channelFormat.find(
        (cf) => cf.invProcess?.includes(invprocess) && cf.direction === direction
    );
    if (!match?.provider) return [];
    return match.provider.split(',').filter(Boolean);
}

/**
 * Get available formats for a given inv process + direction + provider.
 */
export function getFormatsForProvider(
    channelFormat: ObtChannelFormat[],
    invprocess: string,
    direction: string,
    provider: string
): string[] {
    if (!invprocess || !direction || !provider) return [];
    const match = channelFormat.find(
        (cf) =>
            cf.invProcess?.includes(invprocess) &&
            cf.direction === direction &&
            cf.provider?.includes(provider)
    );
    return match?.format ?? [];
}

/**
 * Get available channel types for a given inv process + direction + provider + format.
 */
export function getChannelTypesForFormat(
    channelFormat: ObtChannelFormat[],
    invprocess: string,
    direction: string,
    provider: string,
    format: string
): string[] {
    if (!invprocess || !direction || !provider || !format) return [];
    const match = channelFormat.find(
        (cf) =>
            cf.invProcess?.includes(invprocess) &&
            cf.direction === direction &&
            cf.provider?.includes(provider) &&
            cf.format?.includes(format)
    );
    return match?.channel ?? [];
}

// ─── Duplicate Validation ────────────────────────────────────────────────────────

/**
 * Check if saving a channel row would create a duplicate (same invprocess with overlapping dates).
 */
export function channelHasDuplicate(
    existingRows: ObtPartnerChannel[],
    rowToSave: Record<string, any>
): boolean {
    const process = rowToSave.invprocess;
    const validFrom = rowToSave.validfrom;
    const validTo = rowToSave.validto;

    console.log('Checking', process, 'Valid From', validFrom,'Valid To', validTo);

    if (!process || !validFrom || !validTo) return false;

    const fromDate = dayjs(validFrom, [DATE_ISO_FORMAT, DATE_DISPLAY_FORMAT]);
    const toDate = dayjs(validTo, [DATE_ISO_FORMAT, DATE_DISPLAY_FORMAT]);

    const overlapping = existingRows.filter((ch) => {
        if (!rowToSave.id) return false;
        if (ch.invprocess !== process) return false;
        // Exclude self (same ID)
        if (ch.id && ch.id === rowToSave.id) return false;
        const chFrom = dayjs(ch.validfrom);
        const chTo = dayjs(ch.validto);
        // Overlap check: start1 <= end2 && start2 <= end1
        return chFrom.isBefore(toDate) || chFrom.isSame(toDate, 'day')
            ? (chTo.isAfter(fromDate) || chTo.isSame(fromDate, 'day'))
            : false;
    });

    return overlapping.length > 0;
}

// ─── Date Formatting ─────────────────────────────────────────────────────────────

export function formatChannelDate(value: string | null | undefined): string {
    if (!value) return '';
    const d = dayjs(value);
    return d.isValid() ? d.format(DATE_DISPLAY_FORMAT) : '';
}

export function formatChannelDateTime(value: string | null | undefined): string {
    if (!value) return '';
    const d = dayjs(value);
    return d.isValid() ? d.format('DD.MM.YYYY HH:mm:ss') : '';
}

// ─── Column Definitions ──────────────────────────────────────────────────────────

export function buildChannelColumns(
    intl: IntlShape,
    channelFormat: ObtChannelFormat[],
    isEditable: boolean
): ColDef[] {
    const invProcessCodes = getInvProcessCodes(channelFormat);

    return [
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.partnerno', defaultMessage: 'Supplier No' }),
            field: 'partnerno',
            checkboxSelection: true,
            minWidth: 130,
            editable: false,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.companycode', defaultMessage: 'Company Code' }),
            field: 'companycode',
            minWidth: 120,
            editable: false,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.invprocess', defaultMessage: 'Inv Process' }),
            field: 'invprocess',
            editable: isEditable,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: { values: invProcessCodes, cellHeight: 30 },
            minWidth: 130,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.validfrom', defaultMessage: 'Valid From' }),
            field: 'validfrom',
            editable: isEditable,
            minWidth: 120,
            valueFormatter: (params) => formatChannelDate(params.value),
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.status', defaultMessage: 'Status' }),
            field: 'status',
            minWidth: 80,
            editable: false,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.validto', defaultMessage: 'Valid To' }),
            field: 'validto',
            editable: isEditable,
            minWidth: 120,
            valueFormatter: (params) => formatChannelDate(params.value),
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.direction', defaultMessage: 'Direction' }),
            field: 'direction',
            editable: isEditable,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: (params: ICellEditorParams) => {
                const values = getDirectionsForProcess(channelFormat, params.data?.invprocess);
                return { values: values.length > 0 ? values : [''], cellHeight: 30 };
            },
            minWidth: 110,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.provider', defaultMessage: 'Provider' }),
            field: 'provider',
            editable: isEditable,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: (params: ICellEditorParams) => {
                const values = getProvidersForDirection(channelFormat, params.data?.invprocess, params.data?.direction);
                return { values: values.length > 0 ? values : [''], cellHeight: 30 };
            },
            minWidth: 110,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.format', defaultMessage: 'Format' }),
            field: 'format',
            editable: isEditable,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: (params: ICellEditorParams) => {
                const values = getFormatsForProvider(channelFormat, params.data?.invprocess, params.data?.direction, params.data?.provider);
                return { values: values.length > 0 ? values : [''], cellHeight: 30 };
            },
            minWidth: 110,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.channeltype', defaultMessage: 'Channel Type' }),
            field: 'channeltype',
            editable: isEditable,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: (params: ICellEditorParams) => {
                const values = getChannelTypesForFormat(channelFormat, params.data?.invprocess, params.data?.direction, params.data?.provider, params.data?.format);
                return { values: values.length > 0 ? values : [''], cellHeight: 30 };
            },
            minWidth: 120,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.routeId', defaultMessage: 'Route ID' }),
            field: 'routeId',
            editable: false,
            minWidth: 100,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.insertTime', defaultMessage: 'Insert Time' }),
            field: 'anlageDatum',
            editable: false,
            minWidth: 150,
            valueFormatter: (params) => formatChannelDateTime(params.value),
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.insertUser', defaultMessage: 'Insert User' }),
            field: 'anlageUser',
            editable: false,
            minWidth: 110,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.updateTime', defaultMessage: 'Update Time' }),
            field: 'aenderungDatum',
            editable: false,
            minWidth: 150,
            valueFormatter: (params) => formatChannelDateTime(params.value),
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.updateUser', defaultMessage: 'Update User' }),
            field: 'aenderungUser',
            editable: false,
            minWidth: 110,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.channels.col.formatVersion', defaultMessage: 'Format Version' }),
            field: 'formatVersion',
            editable: false,
            minWidth: 120,
        },
    ];
}

// ─── New Row Builder ─────────────────────────────────────────────────────────────

export function buildNewChannelRow(partnerIdentity: PartnerIdentity): Record<string, any> {
    return {
        id: undefined,
        partnerno: partnerIdentity.partnerno,
        idpartner: partnerIdentity.idpartner,
        companycode: partnerIdentity.companycode,
        invprocess: '',
        validfrom: dayjs().format(DATE_ISO_FORMAT),
        validto: '2099-12-31',
        status: 'N',
        channeltype: '',
        direction: '',
        format: '',
        count: undefined,
        provider: '',
        newProcess: undefined,
        routeId: '',
    };
}

// ─── Prepare for Save ────────────────────────────────────────────────────────────

/**
 * Cleans a channel row for the GraphQL mutation input.
 * Formats dates, removes non-input fields, sets defaults.
 */
export function prepareChannelForSave(row: Record<string, any>, idpartner: number): Record<string, any> {
    const model = { ...row };
    model.idpartner = idpartner;
    model.status = model.status || 'N';

    // Format dates to ISO
    if (model.validfrom) {
        const vf = dayjs(model.validfrom);
        model.validfrom = vf.isValid() ? vf.format(DATE_ISO_FORMAT) : model.validfrom;
    }
    if (model.validto) {
        const vt = dayjs(model.validto);
        model.validto = vt.isValid() ? vt.format(DATE_ISO_FORMAT) : model.validto;
    }

    // Remove non-input fields
    delete model.__typename;
    delete model.anlageUser;
    delete model.anlageDatum;
    delete model.aenderungUser;
    delete model.aenderungDatum;
    delete model.formatVersion;
    delete model.eskerOnboarded;

    // Ensure required fields have defaults
    model.count = '0';
    model.routeId = model.routeId ?? '';
    model.newProcess = model.newProcess ?? false;
    model.id = model.id ?? 0;

    return model;
}

// ─── Copy Channel Row ────────────────────────────────────────────────────────────

/**
 * Creates a copy of a channel row for duplication.
 * Strips: id, audit fields (anlageUser, anlageDatum, aenderungUser, aenderungDatum), formatVersion.
 * Keeps: invprocess, direction, provider, format, channeltype, validfrom, validto, routeId.
 */
export function buildCopiedChannelRow(source: Record<string, any>, partnerIdentity: PartnerIdentity): Record<string, any> {
    return {
        id: undefined,
        partnerno: source.partnerno ?? partnerIdentity.partnerno,
        idpartner: source.idpartner ?? partnerIdentity.idpartner,
        companycode: source.companycode ?? partnerIdentity.companycode,
        invprocess: source.invprocess ?? '',
        direction: source.direction ?? '',
        provider: source.provider ?? '',
        format: source.format ?? '',
        channeltype: source.channeltype ?? '',
        validfrom: source.validfrom ?? dayjs().format(DATE_ISO_FORMAT),
        validto: source.validto ?? '2099-12-31',
        status: 'N',
        routeId: source.routeId ?? '',
        count: source.count ?? undefined,
        newProcess: source.newProcess ?? undefined,
        // Intentionally omitted: anlageUser, anlageDatum, aenderungUser, aenderungDatum, formatVersion
    };
}
